import { useState, useEffect } from "react";
import MapSection from "./MapSection";
import Sidebar from "./Sidebar";
import useLocations from "./hooks/useLocations";
import useTripForm from "./hooks/useTripForm";
import { useMediaQuery } from "@mantine/hooks";
import {
  Button,
  Box,
  Group,
  Paper,
  Stack,
  Title,
  useMantineTheme,
} from "@mantine/core";
import Stepper from "./Stepper";
import { LeftArrow, Itinerary } from "./icons";

const IFRAME_WIDTH = 390;
const VITE_APP_BASE_DOMAIN = import.meta.env.VITE_APP_BASE_DOMAIN;

export default function MapLayout({
  step,
  tripType,
  setTripType,
  onNext,
  onBack,
}) {
  const theme = useMantineTheme();
  const isOther = ["other", "large_event"].includes(tripType);
  const hideReview = useMediaQuery("(max-width: 860px)");
  const isMobile = useMediaQuery("(max-width: 550px)");
  const [viewSummary, setViewSummary] = useState(false);

  const {
    locations,
    setLocations,
    updateLocation,
    stopOrderIsValid,
    hasAllLocations,
    hasValidLocation,
  } = useLocations(tripType);

  const {
    formData,
    updateFormField,
    isValidEmail,
    contactInfoData,
    hasAdditionalDetails,
    tripInfoData,
    tripInfoDataParam,
    stringifiedLocations,
    tripTypeTitle,
  } = useTripForm(isOther, locations, tripType);

  const [iframeHeight, setIframeHeight] = useState(0);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === "itineraryResize") {
        setIframeHeight(event.data.height);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem("miniGetAQuoteData");

    if (!stored) return;

    try {
      const data = JSON.parse(stored);

      updateFormField("useDiy", true);

      if (data.isMobile) return;

      if (data.locations) {
        setLocations(data.locations);
        updateLocation(1, data.locations[1]);
      }

      if (data.numPassengers) {
        updateFormField("pax", data.numPassengers);
      }
    } catch (err) {
      console.error("Failed to parse session data", err);
    } finally {
      sessionStorage.removeItem("miniGetAQuoteData");
    }
  }, []);

  const baseHeight = iframeHeight
    ? Math.min(
        iframeHeight + (hasAdditionalDetails ? 90 : 50),
        window.innerHeight * 0.9,
      )
    : "auto";

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        height: isMobile ? "auto" : "100vh",
        width: "100%",
        maxWidth: "100%",
        alignItems: "stretch",
        overflowX: "hidden",
        overflowY: isMobile ? "visible" : "hidden",
      }}
    >
      <Box
        style={{
          width: !isMobile ? 350 : "100%",
          display: isMobile && viewSummary ? "none" : "flex",
          flexDirection: "column",
          borderRight: !isMobile ? "1px solid #eee" : "none",
          height: isMobile ? "auto" : "100%",
          overflowY: isMobile ? "visible" : "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box
          p={20}
          bg="white"
          style={{
            borderBottom: "1px solid #D3D3D3",
            boxShadow: "0 6px 6px -6px rgba(0, 0, 0, 0.25)",
            flexShrink: 0,
            position: isMobile ? "sticky" : "relative",
            top: 0,
            zIndex: 10,
          }}
        >
          <Stepper step={step} />
          <Group justify="space-between">
            <Button
              variant="subtle"
              px={0}
              onClick={onBack}
              leftSection={<LeftArrow stroke={1.5} width={12} height={12} />}
            >
              Back
            </Button>
            {isMobile && (
              <Button
                variant="subtle"
                px={0}
                onClick={() => setViewSummary(true)}
                leftSection={<Itinerary stroke={1.5} width={12} height={12} />}
              >
                View trip summary
              </Button>
            )}
          </Group>
        </Box>

        <Sidebar
          isValidEmail={isValidEmail}
          step={step}
          isOther={isOther}
          tripType={tripType}
          setTripType={setTripType}
          updateLocation={updateLocation}
          locations={locations}
          setLocations={setLocations}
          formData={formData}
          updateFormField={updateFormField}
          tripTypeTitle={tripTypeTitle}
          stopOrderIsValid={stopOrderIsValid}
          hasAllLocations={hasAllLocations}
          onNext={onNext}
          contactInfoData={contactInfoData}
          tripInfoData={tripInfoData}
          isMobile={isMobile}
        />
      </Box>

      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "auto" : "100%",
          position: "relative",
          overflowY: isMobile ? "visible" : "hidden",
          padding: isMobile ? 10 : 0,
        }}
      >
        {isMobile && viewSummary && (
          <Box
            style={{
              padding: 10,
              position: "relative",
              flexShrink: 0,
              background: "#FAFAFA",
            }}
          >
            <Button
              variant="white"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                background: "transparent",
              }}
              onClick={() => setViewSummary(false)}
            >
              ✕
            </Button>
            <Title>Itinerary · {tripTypeTitle()}</Title>
          </Box>
        )}

        <Box
          style={{
            display: isMobile && !viewSummary ? "none" : "block",
            height: isMobile ? (viewSummary ? 325 : 400) : "100%",
            width: "100%",
            flexShrink: 0,
          }}
        >
          <MapSection
            locations={locations}
            setLocations={setLocations}
            tripType={tripType}
            iframeWidth={IFRAME_WIDTH}
            hideReview={hideReview}
            isMobile={isMobile}
            isOther={isOther}
          />
        </Box>

        {isMobile && (
          <Box
            style={{
              display: viewSummary ? "block" : "none",
              height: "auto",
              background: "#FAFAFA",
              marginTop: 20,
            }}
          >
            <iframe
              name="itinerary_frame"
              src={`${VITE_APP_BASE_DOMAIN}/api/v1/widget/get_a_quote_itinerary?trip_type=${tripType}&locations=${stringifiedLocations}&trip_info_data=${tripInfoDataParam}`}
              style={{
                width: "100%",
                border: "none",
                height: iframeHeight + (hasAdditionalDetails ? 90 : 50),
              }}
              scrolling="no"
            />
          </Box>
        )}

        {!isMobile &&
          (hasValidLocation || hasAdditionalDetails) &&
          !hideReview && (
            <Paper
              pt={35}
              shadow="xl"
              radius="md"
              pos="absolute"
              top="60px"
              left="10px"
              w={IFRAME_WIDTH}
              h={baseHeight}
              mah="90vh"
              style={{
                maxHeight: "min(90vh, -webkit-fill-available)",
                paddingRight: "10px",
                paddingBottom: "10px",
                paddingLeft: "10px",
                overflow: "hidden",
              }}
            >
              {/* <div
                style={
                  tripType === "custom" && locations.length > 2
                    ? {
                        position: "relative",
                        top: 0,
                        left: 0,
                        width: "125%",
                        height: "125%",
                        transform: "scale(0.8)",
                        transformOrigin: "top left",
                      }
                    : { width: "100%", height: "100%" }
                }
              > */}
              <iframe
                name="itinerary_frame"
                src={`${VITE_APP_BASE_DOMAIN}/api/v1/widget/get_a_quote_itinerary?trip_type=${tripType}&locations=${stringifiedLocations}&trip_info_data=${tripInfoDataParam}`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
              {/* </div> */}
            </Paper>
          )}
      </Box>
    </Box>
  );
}
